/*
 * Targeted announcement bar.
 *
 * Shows the targeted bar (#ann-bar-with-refer) when the visitor arrived from a
 * matching paid source/medium combination, otherwise shows the normal bar
 * (#ann-bar-without-refer). Eligibility is remembered for the browser tab so the
 * offer survives internal navigation that drops the tracking parameters.
 *
 * The affiliate exclusions below are mandatory: they always win, they also clear
 * any remembered eligibility, and they cannot be overridden by configuration.
 */
(function (global) {
  'use strict';

  var SESSION_KEY = 'mps:targeted-announcement';
  var SESSION_ELIGIBLE = '1';
  var SESSION_EXCLUDED = 'excluded';

  var TARGETED_BAR_ID = 'ann-bar-with-refer';
  var NORMAL_BAR_ID = 'ann-bar-without-refer';

  function freeze(value) {
    return typeof Object.freeze === 'function' ? Object.freeze(value) : value;
  }

  /*
   * Not merchant-configurable and not mutable through the exported API.
   * Affiliate traffic must never see the paid offer.
   */
  var EXCLUSIONS = freeze([
    freeze({ param: 'ref', value: 'thepetpicks' }),
    freeze({ param: 'wct-an', value: 'goaffpro' })
  ]);

  var DEFAULTS = freeze({
    sourceParam: 'source',
    sourceValue: 'google',
    mediumParam: 'medium',
    mediumValue: 'cpc',
    exclusions: EXCLUSIONS
  });

  function containsInsensitive(value, needle) {
    if (typeof value !== 'string' || typeof needle !== 'string') return false;
    if (value === '' || needle === '') return false;
    return value.toLowerCase().indexOf(needle.toLowerCase()) !== -1;
  }

  /**
   * Checks every value for a key, so a harmless first duplicate cannot hide a
   * later eligible or excluded value.
   */
  function anyValueContains(params, name, needle) {
    if (typeof name !== 'string' || name === '') return false;

    var values;
    try {
      values = typeof params.getAll === 'function' ? params.getAll(name) : [params.get(name)];
    } catch (error) {
      return false;
    }
    if (!values || !values.length) return false;

    for (var i = 0; i < values.length; i++) {
      if (containsInsensitive(values[i], needle)) return true;
    }
    return false;
  }

  function resolveStorage(config) {
    if (config && Object.prototype.hasOwnProperty.call(config, 'storage')) {
      return config.storage;
    }
    try {
      return global.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  function remember(storage, value) {
    try {
      storage.setItem(SESSION_KEY, value);
    } catch (error) {
      /* blocked or full storage must never break rendering */
    }
  }

  function recallState(storage) {
    try {
      return storage.getItem(SESSION_KEY);
    } catch (error) {
      return null;
    }
  }

  /* A detached copy, so nothing handed out can reach the live exclusion rules. */
  function exclusionsCopy() {
    var copy = [];
    for (var i = 0; i < EXCLUSIONS.length; i++) {
      copy.push({ param: EXCLUSIONS[i].param, value: EXCLUSIONS[i].value });
    }
    return copy;
  }

  function normaliseConfig(config) {
    var source = config || {};

    return {
      sourceParam: source.sourceParam || DEFAULTS.sourceParam,
      sourceValue: source.sourceValue || DEFAULTS.sourceValue,
      mediumParam: source.mediumParam || DEFAULTS.mediumParam,
      mediumValue: source.mediumValue || DEFAULTS.mediumValue,
      /* Deliberately ignores config: exclusions are mandatory. */
      exclusions: exclusionsCopy()
    };
  }

  function readSearch(config) {
    if (config && typeof config.search === 'string') return config.search;
    try {
      return global.location.search || '';
    } catch (error) {
      return '';
    }
  }

  function resolveDocument(config) {
    if (config && config.document) return config.document;
    return typeof global.document !== 'undefined' ? global.document : null;
  }

  function isDesignMode(config) {
    if (config && typeof config.designMode === 'boolean') return config.designMode;
    try {
      return !!(global.Shopify && global.Shopify.designMode);
    } catch (error) {
      return false;
    }
  }

  /**
   * Parses the query string. Returns null if URLSearchParams is unavailable or
   * throws, rather than retrying the same failing constructor.
   */
  function parseParams(search) {
    try {
      return new URLSearchParams(search);
    } catch (error) {
      return null;
    }
  }

  /**
   * Decides whether the targeted bar should be shown, and keeps the session
   * flag in sync. Never throws.
   */
  function evaluate(config) {
    if (config && config.enabled === false) return false;

    var settings = normaliseConfig(config);
    var storage = resolveStorage(config);
    var state = recallState(storage);
    var params = parseParams(readSearch(config));

    // Unparseable query string: never grant new eligibility, keep what the tab knows.
    if (!params) return state === SESSION_ELIGIBLE;

    // Reads the private rules, never a caller-reachable copy.
    var excluded = EXCLUSIONS.some(function (rule) {
      return anyValueContains(params, rule.param, rule.value);
    });

    // Exclusion is sticky for the tab: it clears eligibility and outlives later paid parameters.
    if (excluded) {
      remember(storage, SESSION_EXCLUDED);
      return false;
    }
    if (state === SESSION_EXCLUDED) return false;

    var matched =
      anyValueContains(params, settings.sourceParam, settings.sourceValue) &&
      anyValueContains(params, settings.mediumParam, settings.mediumValue);

    if (matched) {
      remember(storage, SESSION_ELIGIBLE);
      return true;
    }

    return state === SESSION_ELIGIBLE;
  }

  /**
   * Shows exactly one bar. Missing elements are ignored.
   */
  function apply(doc, eligible) {
    var scope = doc || null;
    if (!scope || typeof scope.getElementById !== 'function') return;

    var targeted = scope.getElementById(TARGETED_BAR_ID);
    var normal = scope.getElementById(NORMAL_BAR_ID);

    // Only hide the normal bar if the targeted bar is really there to replace it.
    var showTargeted = !!(eligible && targeted && targeted.style);

    if (targeted && targeted.style) targeted.style.display = showTargeted ? 'block' : 'none';
    if (normal && normal.style) normal.style.display = showTargeted ? 'none' : 'block';
  }

  /**
   * True when a Shopify design-mode event belongs to the targeted bar. Handles
   * the bar itself, a block inside it (closest), and the section wrapper that
   * contains it (querySelector).
   */
  function isTargetedSectionEvent(event) {
    var target = event && event.target;
    if (!target) return false;
    if (target.id === TARGETED_BAR_ID) return true;

    var selector = '#' + TARGETED_BAR_ID;

    try {
      if (typeof target.closest === 'function' && target.closest(selector)) return true;
    } catch (error) {
      /* a hostile or partial element must not break rendering */
    }

    try {
      return typeof target.querySelector === 'function' && !!target.querySelector(selector);
    } catch (error) {
      return false;
    }
  }

  /**
   * Pure visibility decision. Selecting the targeted section in the Theme Editor
   * previews its bar; everything else follows normal eligibility. Exactly one
   * bar is shown either way.
   */
  function resolveVisibility(config, state) {
    var current = state || {};
    if (current.designMode && current.targetedSectionSelected) return true;
    return evaluate(config);
  }

  /**
   * Re-runs targeting when Shopify swaps section DOM in the Theme Editor, so the
   * replacement markup gets the correct visibility. Returns the event names
   * registered. Safe to call without a document.
   */
  function listen(config) {
    var settings = config || {};
    var doc = resolveDocument(settings);
    if (!doc || typeof doc.addEventListener !== 'function') return [];

    var registered = [];
    var targetedSectionSelected = false;

    function render() {
      apply(
        resolveDocument(settings),
        resolveVisibility(settings, {
          designMode: isDesignMode(settings),
          targetedSectionSelected: targetedSectionSelected
        })
      );
    }

    function on(name, handler) {
      doc.addEventListener(name, handler);
      registered.push(name);
    }

    function select(event) {
      if (isTargetedSectionEvent(event)) targetedSectionSelected = true;
      render();
    }

    function deselect(event) {
      if (isTargetedSectionEvent(event)) targetedSectionSelected = false;
      render();
    }

    on('shopify:section:load', render);
    on('shopify:section:unload', render);
    on('shopify:section:select', select);
    on('shopify:section:deselect', deselect);
    on('shopify:block:select', select);
    on('shopify:block:deselect', deselect);

    return registered;
  }

  function init(config) {
    var settings = config || {};
    var eligible = evaluate(settings);

    apply(resolveDocument(settings), eligible);
    if (settings.listen !== false) listen(settings);

    return eligible;
  }

  var api = {
    SESSION_KEY: SESSION_KEY,
    SESSION_ELIGIBLE: SESSION_ELIGIBLE,
    SESSION_EXCLUDED: SESSION_EXCLUDED,
    DEFAULTS: DEFAULTS,
    EXCLUSIONS: EXCLUSIONS,
    TARGETED_BAR_ID: TARGETED_BAR_ID,
    NORMAL_BAR_ID: NORMAL_BAR_ID,
    evaluate: evaluate,
    apply: apply,
    resolveVisibility: resolveVisibility,
    isTargetedSectionEvent: isTargetedSectionEvent,
    listen: listen,
    init: init
  };

  global.TargetedAnnouncement = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
